import { api } from "./client";
import type { PlayerItem } from "../types";

export interface PlayerGemStack {
  gem_type: string;
  level: number;
  quantity: number;
  stats: Record<string, number>;
}

export interface SynthesizeGemResult {
  success: boolean;
  message: string;
  success_rate: number;
  gold_spent: number;
  gems: PlayerGemStack[];
}

export interface GemCraftConfig {
  craftable_types: string[];
  max_level: number;
  auto_craft_max_output_level: number;
  stats_by_level: Record<string, Record<string, number>>;
  synthesis_by_level: Record<string, { gold_cost: number; success_rate: number; to_level: number }>;
  sell_gold_by_level: Record<string, number>;
  luck_bonus_per_level: number;
  gem_exchange?: {
    crypto_cost: number;
    gold_cost_by_level: Record<string, number>;
  };
}

export interface BlacksmithConfig {
  craft_levels: number[];
  armor_slots: string[];
  shards_required: Record<string, number>;
  crypto_required?: Record<string, number>;
  previous_level?: Record<string, number>;
  armor_gold_cost_base: number;
  armor_gold_cost_per_level: number;
  stones_required: Record<string, number>;
  legendary_gold_cost_base: number;
  legendary_gold_cost_per_level: number;
  gem_craft?: GemCraftConfig;
}

export interface CraftArmorResult {
  message: string;
  item: PlayerItem;
  shards_spent: number;
  crypto_spent?: number;
  gold_spent: number;
  shard_level: number;
}

export interface LegendaryUpgradeResult {
  message: string;
  item: PlayerItem;
  stones_spent: number;
  gold_spent: number;
  stone_level: number;
}

export interface AutoCraftResult {
  message: string;
  attempts: number;
  successes: number;
  failures: number;
  gold_spent: number;
  luck_spent?: number;
  gems_created: Record<string, number>;
  gems: PlayerGemStack[];
}

export interface AutoCraftFullPreview {
  attempts: number;
  gold_needed: number;
  gold_owned: number;
  luck_owned: number;
  luck_stones_required: number;
  luck_stones_to_spend: number;
  luck_stones_shortfall: number;
  guaranteed_ok: boolean;
  needs_confirm: boolean;
  max_output_level: number;
  message: string;
}

export interface SellGemResult {
  message: string;
  gold_gained: number;
  gems: PlayerGemStack[];
}

export interface ExchangeGemResult {
  message: string;
  gold_spent: number;
  crypto_spent: number;
  gems: PlayerGemStack[];
}

export const blacksmithApi = {
  getConfig: () => api.get<BlacksmithConfig>("/blacksmith/config"),

  synthesizeGem: (gem_type: string, from_level: number, luck_level = 0) =>
    api.post<SynthesizeGemResult>("/blacksmith/synthesize-gem", {
      gem_type,
      from_level,
      luck_level,
    }),

  sellGem: (gem_type: string, level: number, quantity = 1) =>
    api.post<SellGemResult>("/blacksmith/sell-gem", { gem_type, level, quantity }),

  exchangeGem: (from_gem_type: string, to_gem_type: string, level: number) =>
    api.post<ExchangeGemResult>("/blacksmith/exchange-gem", {
      from_gem_type,
      to_gem_type,
      level,
    }),

  autoCraftGems: () => api.post<AutoCraftResult>("/blacksmith/auto-craft-gems"),

  autoCraftGemsFullPreview: () =>
    api.get<AutoCraftFullPreview>("/blacksmith/auto-craft-gems-full/preview"),

  autoCraftGemsFull: (confirm_without_luck = false) =>
    api.post<AutoCraftResult>("/blacksmith/auto-craft-gems-full", { confirm_without_luck }),

  craftArmor: (slot: string, item_level: number, base_item_id?: number) =>
    api.post<CraftArmorResult>("/blacksmith/craft-armor", {
      slot,
      item_level,
      ...(base_item_id != null ? { base_item_id } : {}),
    }),

  legendaryUpgrade: (item_id: number) =>
    api.post<LegendaryUpgradeResult>("/blacksmith/legendary-upgrade", { item_id }),
};
